<template>
  <div class="wrapper information-page">
    <parallax
      class="section page-chat-header header-filter"
      :style="headerStyle"
    >
      <div class="container">
        <div class="md-layout">
          <div
            class="md-layout-item md-size-66 md-small-size-100 md-xsmall-size-100 md-medium-size-66 mx-auto md-scrollbar"
          >
            <h1 class="title">Was steckt dahinter?</h1>
          </div>
        </div>
      </div>
    </parallax>
    <div class="main main-raised">
      <div class="section">
        <div class="container">
          <div class="md-layout md-size-100 mx-auto md-alignment-top-center">
            <div class="md-layout-item md-size-100">
              <div class="document-body">
                <article
                  class="document-content"
                  aria-label="Was steckt dahinter?"
                >
                  <nav
                    class="document-navigation"
                    aria-label="Inhalt von Was steckt dahinter"
                  >
                    <p class="document-navigation-title">Inhalt</p>
                    <a
                      href="#/theory"
                      @click.prevent="scrollToSection('theory-overview')"
                      >Die Theorie kurz erklärt</a
                    >
                    <a
                      href="#/theory"
                      @click.prevent="scrollToSection('theory-goals')"
                      >Ziele gemeinsam besprechen</a
                    >
                    <a
                      href="#/theory"
                      @click.prevent="scrollToSection('theory-motivation')"
                      >Motivation zur Zielarbeit</a
                    >
                    <a
                      href="#/theory"
                      @click.prevent="scrollToSection('theory-sources')"
                      >Quellen und Literatur</a
                    >
                  </nav>
                  <h2 id="theory-overview" tabindex="-1">
                    Die Theorie kurz erklärt
                  </h2>
                  <p>
                    Die Control-IT Webseite stellt die Selbstreflexion und die
                    Arbeit an persönlichen Lern -und Verhaltenszielen in den
                    Fokus. Hier sind eine kontinuierliche Reflexion, Auswertung
                    und Phasen des konstruktiven Feedbacks notwendig. Nach einer
                    Analyse und einem geeigneten Diagnoseverfahren werden in
                    Absprache mit dem*der Jugendlichen Ziele aufgestellt.
                  </p>
                  <p>
                    Der*die Jugendliche soll lernen, sich selbst einzuschätzen
                    und an konkreten Zielen zu arbeiten. Das Erleben von
                    Selbstwirksamkeit wird dabei angestrebt.
                    Verhaltensmodifikationen stehen in enger Verbindung mit
                    Selbstreflexion und der dann damit verbundenen Evaluation.
                    Das Potenzial über sich selbst, über das Umfeld oder mit
                    anderen gemeinsam zu reflektieren &ouml;ffnet den Zugang zu
                    bewussten Handlungsveränderungen und erm&ouml;glicht
                    h&ouml;here Formen des Lernens <small>(Greif, 2008)</small>.
                  </p>
                  <p>
                    Die Control-IT Webseite stellt eine attraktive Variante zum
                    Selbstreflektieren und Gedanken bündeln dar. Hier kann
                    der*die Jugendliche in einem Chat mit dem Modellcharakter
                    das Erlebte austauschen, Ziele setzen, überwachen und
                    auswerten. Den Jugendlichen wird mit diesen modernen Formen
                    der Reflexion ein strukturiertes Mittel an die Hand gegeben.
                    ähnlich wie bereits umfassend erforschte Tagebucheinträge,
                    mit denen Verhalten, Emotionen, Handlungsweisen und
                    Entscheidungen erkundet werden k&ouml;nnen. Letztendlich
                    f&ouml;rdert dies Selbstwirksamkeit, Selbsterkenntnis und
                    langfristig auch die Fähigkeit zur Planung neuer
                    Handlungsmuster.
                  </p>
                  <p>
                    Ausgehend von einer treffenden Selbsteinschätzung, ggf. mit
                    Hinweisen von au&szlig;en werden Wochenziele aufgestellt.
                    Jede Woche kann neu entschieden werden: Wird am gleichen
                    Ziel weitergearbeitet, oder wird ein neues aufgestellt? Die
                    Ziele sollten weder überfordernd noch unterfordernd sein. An
                    der Formel <em>Ist-Stand + 1</em> kann sich orientiert
                    werden, <em>plus 1</em> bezieht sich dabei auf den nächsten
                    Entwicklungsschritt.
                  </p>
                  <h2 id="theory-goals" tabindex="-1">
                    Ziele gemeinsam besprechen
                  </h2>
                  <aside
                    class="document-notice"
                    aria-label="Hinweise zur Zielbesprechung"
                  >
                    <ul>
                      <li>
                        Der*die Jugendliche sollte sich das Ziel nach
                        Individuums-Umgebungsanalyse bestm&ouml;glich selbst
                        auswählen
                      </li>
                      <li>
                        Anleiter*in gibt Feedback zum ausgewählten Ziel, ggf.
                        wird das Ziel gemeinsam angepasst bzw. präzisiert
                      </li>
                      <li>
                        Den Fokus bewahren, weniger ist manchmal mehr. Entlasten
                        Sie den*die Jugendlichen, indem sie sich auf ein Ziel
                        fokussieren.
                      </li>
                      <li>
                        Das Ziel sollte sich an den SMART Kriterien
                        <small>(Drucker 1977)</small>
                        orientieren
                      </li>
                    </ul>
                  </aside>
                  <p>
                    <small>Maddux (2000)</small> zeigte in Untersuchungen, dass
                    Menschen, die an ihre Wirksamkeit und Fähigkeiten glauben,
                    Probleme effizienter l&ouml;sen, als Probanden mit niedrigem
                    Vertrauen in die eigenen kognitiven Fähigkeiten. Neben der
                    Bestärkung der Schüler*innen an das Glauben in ihre
                    Fähigkeiten, ist auch die eigene Erwartungshaltung
                    entscheidend und kann sich positiv auf Zielerreichungen und
                    Leistungen der Schüler*innen auswirken.
                    <small>(Mehlhorn, 2022)</small>
                  </p>
                  <p>
                    Mehr Informationen finden Sie im Buch
                    <em
                      ><a
                        href="https://www.beltz.de/fachmedien/psychologie/produkte/details/47411-impulskontrolle-bei-jugendlichen.html"
                        >Impulskontrolle bei Jugendlichen
                        <i class="material-icons">book</i></a
                      > </em
                    ><small>(Mehlhorn, Beltz, 2022)</small>.
                  </p>
                  <h2 id="theory-motivation" tabindex="-1">
                    Motivation zur Zielarbeit
                  </h2>
                  <aside
                    class="document-notice"
                    aria-label="Tipps für die Motivation zur Zielarbeit"
                  >
                    <p>
                      Verstärkung bei positivem Verhalten bringt langfristig
                      bessere Erfolge als negative Konsequenzen bei
                      Fehlverhalten. Einigen sie sich mit den Jugendlichen auf
                      Belohnungen beim Erreichen von Zielen oder bei
                      kontinuierlicher Reflexion und führen sie ein
                      Verstärkersystem ein.<br /><br />Beispiel: Für eine Anzahl
                      von Zielerreichung über 60% bekommt der*die Jugendliche
                      einen Token z. B. 😊, Stern, Chip. Hat er*sie eine gewisse
                      Anzahl erreicht, erhält er*sie die vorher vereinbarte
                      Belohnung. Beispiele für Belohnung sind:<br />
                    </p>
                    <ul>
                      <li>Gemeinsames Kickern, Brett- oder Kartenspiel</li>
                      <li>Gutschein für Medienzeit</li>
                      <li>
                        Gutschein für Rausgehen während der Unterrichtszeit
                      </li>
                      <li>Eis essen, Sü&szlig;igkeiten, Pizza essen</li>
                      <li>Gemeinsam backen</li>
                      <li>Youtube Video ansehen</li>
                      <li>Ausflug</li>
                    </ul>
                  </aside>
                  <h2 id="theory-sources" tabindex="-1">
                    Quellen und Literatur
                  </h2>
                  <div class="document-sources">
                    <p>
                      Drucker, Peter F. (1977). People and Performance: The Best
                      of Peter Drucker on Management. New York: Harper&rsquo;s
                      College Press.
                    </p>
                    <p>
                      Greif, S. (2008). Coaching und ergebnisorientierte
                      Selbstreflexion: Theorie, Forschung und Praxis des Einzel-
                      und Gruppencoachings. G&ouml;ttingen: Hogrefe
                    </p>
                    <p>
                      Maddux, J. E. (2000). Self-Efficacy: The Power of
                      Believing you can. In: Synder, C. R., Lopez, S.J (Hrsg.).
                      Handbook of positive psychology. New York: Oxford
                      University Press.
                    </p>
                    <p>
                      Mehlhorn, A. K. (2022). Impulskontrolle bei Jugendlichen.
                      Trainingsmanual für Psychotherapie, Schule und Beratung.
                      Weinheim: Beltz.
                    </p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import scrollToSection from "@/utils/scrollToSection";

export default {
  name: "theory-pageView",
  bodyClass: "theory-page",
  props: {
    header: {
      type: String,
      default: require("@/assets/img/backgrounds/background4.png"),
    },
  },
  computed: {
    headerStyle() {
      return {
        backgroundImage: `url(${this.header})`,
      };
    },
  },
  methods: { scrollToSection },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/information-pages.scss";
</style>
